// F13〜F16: 物語プロジェクト（newgames/tools/projects/<projectId>/）の管理。
// 1 プロジェクト = 1 小説 = 1 ゲーム。ゲームを削除してもプロジェクトが残るため、いつでも再生成できる。
// projects/*/story/（小説原文コピー）は .gitignore 対象、それ以外の設計データは Git 管理対象。
import { existsSync, mkdirSync, readdirSync, readFileSync, statSync, writeFileSync, rmSync } from 'node:fs';
import path from 'node:path';
import { TOOLS_DIR, REPO_ROOT, containedPath } from './repo-paths.mjs';
import { readJsonIfExists, writeJson } from './json-utils.mjs';
import { auditLog } from './logger.mjs';
import { updateGameMeta } from './games.mjs';
import { enrichGenerationLogStore } from './generation-cost.mjs';

export const PROJECTS_DIR = path.join(TOOLS_DIR, 'projects');
export const PROJECT_META_FILE = 'project.json';

/** 実行順に並んだパイプラインのステップ定義（UI/CLI の進捗表示に使う）。 */
export const STORY_STEPS = [
    { id: 'analyze', label: '① 物語解析（要約シート・キャスト・企画）' },
    { id: 'scaffold', label: '② ベースデータ生成' },
    { id: 'imagePrompts', label: '③ キャラ生成' },
    { id: 'worldDesign', label: '④ 初期ワールド設計' },
    { id: 'startScreen', label: '⑤ 開始画面設計（背景・立ち絵）' },
    { id: 'requestText', label: '⑥-1 AI 依頼文の生成・編集' },
    { id: 'generateJson', label: '⑥-2 JSON 生成（LLM 実行）' },
    { id: 'import', label: '⑥-3 取り込み・検証' },
];

export const PIPELINE_STAGE_ORDER = ['analyze', 'scaffold', 'imagePrompts', 'worldDesign', 'startScreen', 'requestText', 'generateJson', 'import'];

export function invalidatedPipelineStages(stageId, { includeStage = false } = {}) {
    const index = PIPELINE_STAGE_ORDER.indexOf(stageId);
    if (index < 0) throw new Error(`不明な工程です: ${stageId}`);
    // ⑤ 開始画面設計は見た目専用のサイドステージ: 再生成・差し替えを繰り返しても
    // ⑥（依頼文・JSON・取り込み）の有料成果物へは波及させない
    if (stageId === 'startScreen') return includeStage ? ['startScreen'] : [];
    return PIPELINE_STAGE_ORDER.slice(index + (includeStage ? 0 : 1));
}

/** 再実行時に破棄する後工程の成果物と進捗を一元管理する。 */
export function invalidatePipelineFrom(projectId, stageId, { includeStage = false } = {}) {
    requireProjectDir(projectId);
    const invalidatedStages = invalidatedPipelineStages(stageId, { includeStage });
    const invalidated = new Set(invalidatedStages);
    const p = projectPaths(projectId);
    const remove = (target) => rmSync(target, { recursive: true, force: true });

    if (invalidated.has('analyze')) remove(p.analysis);
    if (invalidated.has('imagePrompts')) {
        remove(p.imagePromptsDir);
        remove(path.join(p.dir, 'meshy-tasks.json'));
    }
    if (invalidated.has('worldDesign')) {
        remove(p.worldDesign);
        remove(p.worldDesignMd);
    }
    // 開始画面素材は ③（キャラ外見）・④（世界観）に依存するため、上流の再実行で作り直しになる。
    // ゲームへ適用済みの画像は次回の ⑤ 再適用まで残る（③の画像と同じ扱い）
    if (invalidated.has('startScreen')) remove(p.startScreenDir);
    if (invalidated.has('requestText')) remove(p.request);
    if (invalidated.has('generateJson')) remove(p.generatedDir);

    const before = readProjectMeta(projectId);
    const oldGameId = before.gameId ?? null;
    const invalidatedStepIds = new Set(invalidatedStages);
    if (invalidated.has('imagePrompts')) {
        invalidatedStepIds.add('castImages');
        invalidatedStepIds.add('castModels');
    }
    updateProjectMeta(projectId, (meta) => {
        const steps = { ...(meta.steps ?? {}) };
        for (const step of invalidatedStepIds) delete steps[step];
        return {
            ...meta,
            ...(invalidated.has('scaffold') ? { gameId: null } : {}),
            steps,
            previewRev: (meta.previewRev ?? 0) + 1,
        };
    });
    if (invalidated.has('scaffold') && oldGameId) {
        try { updateGameMeta(oldGameId, (gameMeta) => ({ ...gameMeta, storyProjectId: null })); } catch { /* game未実体化 */ }
    }
    return {
        stageId,
        includeStage,
        invalidatedStages,
        oldGameId,
        clearsPreview: invalidated.has('scaffold') || invalidated.has('import'),
        clearsAssets: invalidated.has('imagePrompts'),
    };
}

export function isValidProjectId(id) {
    return typeof id === 'string' && /^[a-z0-9][a-z0-9-]{0,63}$/.test(id);
}

export function requireProjectDir(projectId) {
    if (!isValidProjectId(projectId)) throw new Error(`不正な projectId です: ${projectId}`);
    const dir = containedPath(PROJECTS_DIR, projectId);
    if (!existsSync(path.join(dir, PROJECT_META_FILE))) {
        throw new Error(`プロジェクトが見つかりません: ${projectId}（newgames/tools/projects/ 配下）`);
    }
    return dir;
}

export function projectPaths(projectId) {
    const dir = containedPath(PROJECTS_DIR, projectId);
    return {
        dir,
        meta: path.join(dir, PROJECT_META_FILE),
        novel: path.join(dir, 'story', 'original.txt'),
        analysis: path.join(dir, 'analysis.json'),
        imagePromptsDir: path.join(dir, 'image-prompts'),
        imagePromptsIndex: path.join(dir, 'image-prompts', 'index.json'),
        imagesDir: path.join(dir, 'image-prompts', 'images'),
        generationLogs: path.join(dir, 'generation-logs.json'),
        worldDesign: path.join(dir, 'world-design.json'),
        worldDesignMd: path.join(dir, 'world-design.md'),
        startScreenDir: path.join(dir, 'start-screen'),
        startScreenJson: path.join(dir, 'start-screen', 'start-screen.json'),
        startScreenImagesDir: path.join(dir, 'start-screen', 'images'),
        request: path.join(dir, 'request.md'),
        generatedDir: path.join(dir, 'generated'),
        generatedStatus: path.join(dir, 'generated', 'status.json'),
        importText: path.join(dir, 'generated', 'import.txt'),
    };
}

function timestampId(now = new Date()) {
    const pad = (n) => String(n).padStart(2, '0');
    return `story-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
}

/**
 * 物語テキストからプロジェクトを新規作成する（ユーザ入力は物語文書のみ）。
 * @param {object} input { novelPath?: 絶対 or リポジトリ相対パス, text?: 本文貼り付け, fileName?: 表示用ファイル名 }
 */
export function createProject(input = {}) {
    let text = typeof input.text === 'string' ? input.text : null;
    let sourcePath = null;
    let fileName = input.fileName ?? null;
    if (!text && input.novelPath) {
        const abs = path.isAbsolute(input.novelPath) ? input.novelPath : path.resolve(REPO_ROOT, input.novelPath);
        if (!existsSync(abs)) throw new Error(`物語ファイルが見つかりません: ${input.novelPath}`);
        text = readFileSync(abs, 'utf-8');
        sourcePath = abs;
        fileName = fileName ?? path.basename(abs);
    }
    if (!text || text.trim().length < 200) {
        throw new Error('物語の本文が短すぎます（200文字以上のテキストファイルまたは貼り付けが必要です）。');
    }
    let projectId = timestampId();
    // 同秒の連続作成に備えて重複回避
    for (let i = 2; existsSync(path.join(PROJECTS_DIR, projectId)); i++) projectId = `${timestampId()}-${i}`;
    const p = projectPaths(projectId);
    mkdirSync(path.dirname(p.novel), { recursive: true });
    writeFileSync(p.novel, text, 'utf-8');
    const meta = {
        formatVersion: '1.0',
        projectId,
        createdAt: new Date().toISOString(),
        novel: { fileName: fileName ?? '(貼り付けテキスト)', chars: text.length, sourcePath },
        gameId: null,
        steps: {},
        usage: { inputTokens: 0, outputTokens: 0, llmCalls: 0 },
    };
    writeJson(p.meta, meta);
    auditLog('story-create', { projectId, chars: text.length, fileName: meta.novel.fileName });
    return meta;
}

/** 解析元の物語文書を差し替える。解析以降は呼び出し側で先に無効化すること。 */
export function replaceProjectNovel(projectId, { text, fileName = null } = {}) {
    requireProjectDir(projectId);
    if (typeof text !== 'string' || text.trim().length < 200) {
        throw new Error('物語の本文が短すぎます（200文字以上のテキストファイルが必要です）。');
    }
    const p = projectPaths(projectId);
    mkdirSync(path.dirname(p.novel), { recursive: true });
    writeFileSync(p.novel, text, 'utf-8');
    const meta = updateProjectMeta(projectId, (current) => ({
        ...current,
        novel: {
            fileName: fileName ? String(fileName).slice(0, 200) : (current.novel?.fileName ?? '(読み込み文書)'),
            chars: text.length,
            sourcePath: null,
        },
    }));
    auditLog('story-novel-replace', { projectId, chars: text.length, fileName: meta.novel.fileName });
    return meta;
}

export function readProjectMeta(projectId) {
    requireProjectDir(projectId);
    return readJsonIfExists(projectPaths(projectId).meta) ?? {};
}

export function updateProjectMeta(projectId, updater) {
    const p = projectPaths(projectId);
    const meta = readProjectMeta(projectId);
    const next = updater(meta) ?? meta;
    writeJson(p.meta, next);
    return next;
}

/** ステップの実行記録（完了時刻・結果サマリ）を meta に残す。 */
export function recordStep(projectId, stepId, info = {}) {
    return updateProjectMeta(projectId, (meta) => ({
        ...meta,
        steps: { ...(meta.steps ?? {}), [stepId]: { at: new Date().toISOString(), ...info } },
    }));
}

/** LLM 使用量（トークン・呼び出し回数）を累積する（コスト可視化用）。 */
export function addUsage(projectId, usage, calls = 1) {
    if (!usage && !calls) return;
    updateProjectMeta(projectId, (meta) => ({
        ...meta,
        usage: {
            inputTokens: (meta.usage?.inputTokens ?? 0) + (usage?.inputTokens ?? 0),
            outputTokens: (meta.usage?.outputTokens ?? 0) + (usage?.outputTokens ?? 0),
            llmCalls: (meta.usage?.llmCalls ?? 0) + calls,
        },
    }));
}

export function readNovel(projectId) {
    requireProjectDir(projectId);
    const p = projectPaths(projectId);
    if (!existsSync(p.novel)) {
        throw new Error('物語原文（story/original.txt）がありません。Git 管理外のため、別マシンでは物語ファイルを再登録してください。');
    }
    return readFileSync(p.novel, 'utf-8');
}

export function readAnalysis(projectId) {
    return readJsonIfExists(projectPaths(projectId).analysis);
}

export function requireAnalysis(projectId) {
    const analysis = readAnalysis(projectId);
    if (!analysis) throw new Error('解析結果（analysis.json）がありません。先に「① 物語解析」を実行してください。');
    return analysis;
}

export function saveAnalysis(projectId, analysis) {
    requireProjectDir(projectId);
    writeJson(projectPaths(projectId).analysis, analysis);
}

export function readWorldDesign(projectId) {
    return readJsonIfExists(projectPaths(projectId).worldDesign);
}

export function readRequestText(projectId) {
    const p = projectPaths(projectId);
    return existsSync(p.request) ? readFileSync(p.request, 'utf-8') : null;
}

export function saveRequestText(projectId, text) {
    requireProjectDir(projectId);
    writeFileSync(projectPaths(projectId).request, String(text), 'utf-8');
    recordStep(projectId, 'requestText', { chars: String(text).length, edited: true });
}

/** プロジェクト一覧（ダッシュボード用の要約）。 */
export function listProjects() {
    if (!existsSync(PROJECTS_DIR)) return [];
    const out = [];
    for (const entry of readdirSync(PROJECTS_DIR, { withFileTypes: true })) {
        if (!entry.isDirectory()) continue;
        const meta = readJsonIfExists(path.join(PROJECTS_DIR, entry.name, PROJECT_META_FILE));
        if (!meta) continue;
        const analysis = readJsonIfExists(path.join(PROJECTS_DIR, entry.name, 'analysis.json'));
        const generated = readJsonIfExists(path.join(PROJECTS_DIR, entry.name, 'generated', 'status.json'));
        out.push({
            projectId: meta.projectId ?? entry.name,
            createdAt: meta.createdAt ?? null,
            novel: meta.novel ?? null,
            gameId: meta.gameId ?? null,
            title: analysis?.game?.title ?? null,
            steps: Object.fromEntries(STORY_STEPS.map(({ id }) => [id, meta.steps?.[id] ? { at: meta.steps[id].at, ok: meta.steps[id].ok !== false } : null])),
            generated: generated ?? null,
            usage: meta.usage ?? null,
        });
    }
    return out.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
}

/** プロジェクト詳細（画面 1 ページ分。重い生成物はファイル存在情報のみ）。 */
export function getProjectDetail(projectId) {
    requireProjectDir(projectId);
    const p = projectPaths(projectId);
    const meta = readProjectMeta(projectId);
    const analysis = readAnalysis(projectId);
    const worldDesign = readWorldDesign(projectId);
    const promptsIndex = readJsonIfExists(p.imagePromptsIndex);
    const generatedStatus = readJsonIfExists(p.generatedStatus);
    const enrichedGenerationLogs = enrichGenerationLogStore(readJsonIfExists(p.generationLogs));
    // 工程カードの常時表示にはコスト索引だけを返す。全文ログは既存の generation-logs API で
    // ダイアログを開いた時だけ取得し、プロジェクト画面の初期応答を肥大化させない。
    const generationLogs = {
        formatVersion: enrichedGenerationLogs.formatVersion,
        runs: enrichedGenerationLogs.runs.map((run) => ({
            id: run.id,
            kind: run.kind,
            mode: run.mode,
            ...(run.batchRunId ? { batchRunId: run.batchRunId } : {}),
            ...(run.batchStep ? { batchStep: run.batchStep } : {}),
            startedAt: run.startedAt,
            finishedAt: run.finishedAt,
            status: run.status,
            ...(Array.isArray(run.castIds) ? { castIds: run.castIds } : {}),
            ...(run.costSummary ? { costSummary: run.costSummary } : {}),
        })),
    };
    return {
        meta,
        steps: STORY_STEPS,
        novelAvailable: existsSync(p.novel),
        analysis,
        worldDesign,
        worldDesignMd: existsSync(p.worldDesignMd) ? readFileSync(p.worldDesignMd, 'utf-8') : null,
        requestText: readRequestText(projectId),
        imagePrompts: promptsIndex ?? null,
        generated: generatedStatus ?? null,
        generationLogs,
        importTextAvailable: existsSync(p.importText),
    };
}

/** 画像プロンプト md の本文を返す（UI の閲覧用）。 */
export function readImagePromptFile(projectId, fileName) {
    requireProjectDir(projectId);
    if (!/^[a-z0-9][a-z0-9_.-]*\.md$/i.test(String(fileName))) throw new Error(`不正なファイル名です: ${fileName}`);
    const abs = containedPath(projectPaths(projectId).imagePromptsDir, fileName);
    if (!existsSync(abs)) throw new Error(`画像プロンプトファイルがありません: ${fileName}`);
    return readFileSync(abs, 'utf-8');
}

/** ディレクトリ配下のファイルを {path(dir 相対), bytes} で列挙する（存在しなければ空）。 */
function dirFileEntries(dir, prefix = '') {
    if (!dir || !existsSync(dir)) return [];
    const out = [];
    for (const entry of readdirSync(dir, { withFileTypes: true })) {
        const abs = path.join(dir, entry.name);
        const rel = prefix ? `${prefix}/${entry.name}` : entry.name;
        if (entry.isDirectory()) out.push(...dirFileEntries(abs, rel));
        else if (entry.isFile()) out.push({ path: rel, bytes: statSync(abs).size });
    }
    return out;
}

/**
 * 容量表示に使うファイル一覧（ゲーム本体と生成3Dモデル）。
 * パスの読み替えと公開対象の選定（toPackageEntries / collectPublishFiles）は呼び出し側が適用する
 * ＝ ⑦ 限定公開・マイ公開ゲームの「容量」と必ず同じ基準になるようにする。
 */
export function projectPackageFileEntries(projectId) {
    requireProjectDir(projectId);
    const gameId = readProjectMeta(projectId).gameId ?? null;
    if (!gameId) return { gameId: null, gameFiles: [], assets: [] };
    return {
        gameId,
        gameFiles: dirFileEntries(path.join(REPO_ROOT, 'newgames', gameId)),
        assets: dirFileEntries(path.join(REPO_ROOT, 'assets', 'newgames', gameId), `newgames/${gameId}`),
    };
}

/**
 * 物語プロジェクト一式（projects/<PID>/）を削除する（2026-07-07 ダッシュボード削除ボタン）。
 * リンク先ゲーム本体・assets/newgames/ の 3D モデル等は削除しない（ゲーム側 meta の storyProjectId のみ解除）。
 * UI 側で確認ダイアログを必ず経由すること。
 */
export function deleteProject(projectId) {
    const dir = requireProjectDir(projectId); // ID 形式検証＋実在確認（パストラバーサル不可）
    const meta = readProjectMeta(projectId);
    let unlinkedGameId = null;
    if (meta.gameId) {
        try {
            updateGameMeta(meta.gameId, (m) => ({ ...m, storyProjectId: null }));
            unlinkedGameId = meta.gameId;
        } catch {
            // ゲームが既に削除済みなら何もしない
        }
    }
    rmSync(dir, { recursive: true, force: true });
    return { projectId, deletedDir: `projects/${projectId}/`, unlinkedGameId };
}
